import {
  DateFormat,
  ProviderAvatarBlock,
  ProviderAvatarBlockProps,
  SkeletonLoader,
} from '@equip.health/ui';
import { useGetProviderInfoWithHistoryByIdQuery } from '~/store/services/providers';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import { DateTime } from 'luxon';

type Props = Omit<ProviderAvatarBlockProps, 'legalName'>;

// Handles API calling for provider avatar block if you only have the user id
export const ProviderAvatarBlockApiWrapper = ({ id, ...rest }: Props) => {
  const { data: providerData, isLoading: isProviderLoading } =
    useGetProviderInfoWithHistoryByIdQuery({
      includeAvatar: true,
      includeOutOfOffice: true,
      providerExternalId: id,
    });

  if (isProviderLoading) {
    return <SkeletonLoader id={`provider-avatar-block-api-wrapper-${id}`} />;
  }
  if (!providerData) {
    return <></>;
  }

  const legalName: ProviderAvatarBlockProps['legalName'] = {
    firstName: providerData?.firstName,
    lastName: providerData?.lastName,
    middleName: providerData?.middleName,
    suffix: providerData?.suffix,
  };
  const displayName: ProviderAvatarBlockProps['displayName'] = {
    firstName: providerData?.displayFirstName,
    lastName: providerData?.displayLastName,
    middleName: providerData?.displayMiddleName,
    suffix: providerData?.displaySuffix,
  };

  const hasDisplayName = displayName.firstName && displayName.lastName;
  const oooEndDate = getLatestOutOfOfficeEndDate(providerData?.outOfOffice);
  const deactivatedDate = DateTime.fromISO(
    providerData?.deactivatedDate,
  ).startOf('day');
  const deactivatedDateFormatted = deactivatedDate?.isValid
    ? deactivatedDate.toFormat(DateFormat.fullMonthDayYear)
    : '';

  return (
    <ProviderAvatarBlock
      id={id}
      avatarUrl={providerData?.avatar?.thumbnailUrl}
      legalName={legalName}
      displayName={hasDisplayName ? displayName : undefined}
      oooDate={oooEndDate}
      shouldShowOOOIcon={oooEndDate !== null}
      deactivatedDate={deactivatedDateFormatted}
      shouldShowCredentials
      shouldShowDeactivatedDate
      {...rest}
    />
  );
};
